/**
 * What was in the box, week by week — public, no sign-in, no member
 * attached to any of it. This is what a QR code on the box itself can point
 * at directly. A member's own account links here too; it's the same page
 * either way.
 */

import { useEffect, useState } from 'preact/hooks';
import { Link } from 'wouter-preact';
import type { JSX } from 'preact';

import { apiGet, getSession } from '../api.js';

export interface BoxWeekSummary {
  id: string;
  season_year: number;
  week_number: number;
  headline: string;
  cover_image: string | null;
}

export function BoxJournalView(): JSX.Element {
  const [weeks, setWeeks] = useState<BoxWeekSummary[] | null>(null);
  const [error, setError] = useState(false);
  const declined = new URLSearchParams(window.location.search).get('declined') === '1';

  const load = () => {
    setError(false);
    apiGet<{ weeks: BoxWeekSummary[] }>('/public/v1/box-weeks')
      .then((result) => setWeeks(result.weeks))
      .catch(() => setError(true));
  };
  useEffect(load, []);

  return <BoxJournalContent weeks={weeks} declined={declined} signedIn={Boolean(getSession())} error={error} onRetry={load} />;
}

/** Pure presentation, also exercised by the dev-only visual review. */
export function BoxJournalContent({ weeks, declined = false, signedIn, error = false, onRetry }: {
  weeks: BoxWeekSummary[] | null; declined?: boolean; signedIn: boolean; error?: boolean; onRetry?: () => void;
}): JSX.Element {

  return (
    <main class="view">
      {declined && (
        <div class="banner">
          You've been removed from the waitlist — we hope to see you next season. You won't get any
          further emails from us about this.
        </div>
      )}

      <header>
        <span class="ff-eyebrow">A season, week by week</span>
        <h1>What's in the box</h1>
        <p class="muted">The harvest, the stories, and the good things in every delivery.</p>
      </header>

      {error && <div class="member-empty" role="alert"><h2>The journal couldn't load.</h2><p>Check your connection and try again.</p><button class="link" onClick={onRetry}>Try again</button></div>}
      {!error && weeks === null && <p class="member-empty" role="status">Opening the journal…</p>}
      {!error && weeks?.length === 0 && <div class="member-empty"><span class="ff-eyebrow">Good things take a little growing</span><h2>The season starts here.</h2><p>Nothing posted yet — check back after the first delivery.</p></div>}

      <div class="pack-grid">
        {(!error ? [...(weeks ?? [])].sort((a, b) => b.season_year - a.season_year || b.week_number - a.week_number) : []).map((week) => (
          <Link key={week.id} href={`/box/${week.id}`} class="pack-card">
            <span class="pack-card-inner">
              <span class="pack-card-media">{week.cover_image ? (
                <img class="pack-card-cover" src={week.cover_image} alt="" loading="lazy" />
              ) : (
                <span class="pack-card-seal" aria-hidden="true">
                  <small>Week</small>{String(week.week_number).padStart(2, '0')}
                </span>
              )}</span>
              <span class="pack-card-label">
                <span class="pack-card-week">
                  {week.season_year} · Week {week.week_number}
                </span>
                <span class="pack-card-headline">{week.headline}</span>
                <span class="pack-card-action">Open this week <span aria-hidden="true">→</span></span>
              </span>
            </span>
          </Link>
        ))}
      </div>

      {!signedIn && (
        <Link href="/login" class="link">
          Member? Sign in
        </Link>
      )}
    </main>
  );
}
